import React, { useState } from 'react';
import { ExclamationTriangleIcon, CheckCircleIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import Modal from '../../ui/Modal';

const LANGUAGE_NAMES = {
  en: 'English', es: 'Spanish', fr: 'French', it: 'Italian', de: 'German',
  pt: 'Portuguese', ar: 'Arabic', zh: 'Chinese', ja: 'Japanese', ko: 'Korean',
  ru: 'Russian', tr: 'Turkish', nl: 'Dutch', pl: 'Polish', sv: 'Swedish',
  sr: 'Serbian', hr: 'Croatian', bs: 'Bosnian',
};

const DESTINATION_INFO = {
  faicon: {
    name: 'FAICON',
    description: 'Your report will be published to the FAICON platform where it can be accessed by authorized users.',
  },
  wordpress: {
    name: 'WordPress (DigWatch)',
    description: 'Your report will be published to WordPress (DigWatch) where it will be publicly accessible.',
  },
};

/**
 * PublishModal Component
 *
 * @param {boolean} isOpen
 * @param {function} onClose
 * @param {function} onConfirm - Called with selected language code
 * @param {string} destination - currently selected destination ('faicon' | 'wordpress')
 * @param {string[]} availableDestinations - list of destinations the user can pick from
 * @param {function} onDestinationChange - called when user switches destination
 * @param {boolean} isUpdate
 * @param {boolean} isPublishing
 * @param {Array} availableLanguages - Languages with status info (filtered to COMPLETED)
 */
const PublishModal = ({
  isOpen,
  onClose,
  onConfirm,
  destination,
  availableDestinations = [],
  onDestinationChange,
  isUpdate = false,
  isPublishing = false,
  availableLanguages = [],
}) => {
  const completedLangs = availableLanguages.filter((l) => l.status === 'COMPLETED');
  const [selectedLang, setSelectedLang] = useState('en');

  const info = DESTINATION_INFO[destination] || DESTINATION_INFO.wordpress;
  const canSwitch = availableDestinations.length > 1 && onDestinationChange;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isUpdate ? 'Update Published Report' : 'Publish Report'}>
      <div className="space-y-4">
        {/* Destination Selector */}
        {canSwitch && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Publish destination
            </label>
            <div className="flex gap-2">
              {availableDestinations.map((dest) => {
                const selected = dest === destination;
                return (
                  <button
                    key={dest}
                    type="button"
                    onClick={() => onDestinationChange(dest)}
                    disabled={isPublishing}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg border-2 transition-all ${
                      selected
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {DESTINATION_INFO[dest]?.name || dest}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Destination Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-blue-900 mb-1">
                Publishing to {info.name}
              </h3>
              <p className="text-sm text-blue-700">{info.description}</p>
            </div>
          </div>
        </div>

        {/* Update Warning */}
        {isUpdate && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-amber-900 mb-1">
                  Warning: This will overwrite existing publish
                </h3>
                <p className="text-sm text-amber-700">
                  The previously published report will be replaced with the new version. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Language Selection (when multiple languages available) */}
        {completedLangs.length > 1 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <GlobeAltIcon className="h-5 w-5 text-gray-600" />
              <h3 className="text-sm font-semibold text-gray-900">Select Language</h3>
            </div>
            <p className="text-xs text-gray-600 mb-3">
              Choose which language version to publish to {info.name}.
            </p>
            <div className="flex flex-wrap gap-2">
              {completedLangs.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setSelectedLang(lang.code)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    selectedLang === lang.code
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                  }`}
                >
                  {LANGUAGE_NAMES[lang.code] || lang.code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isPublishing}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(selectedLang)}
            disabled={isPublishing}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
              isUpdate
                ? 'bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
            }`}
          >
            {isPublishing ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Publishing...
              </span>
            ) : (
              isUpdate ? 'Update Publish' : 'Publish'
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PublishModal;
